import Typography from '~/shared/components/Typography';
import {
  getOperatorText,
  getTagVariant,
} from '~/fleet-manager/components/FleetManagerPage/AutoscaleRulesPage/common/operatorUtils';

import RenderTagGroup from '../../common/RenderTagGroups';

import type { AutoscaleRule } from './hooks/useAutoscaleRulesQuery';

export default function ConditionsRenderer({ data }: { data: AutoscaleRule }) {
  if (!data?.conditions || data.conditions.length === 0) {
    return <span>-</span>;
  }

  // Group conditions by type and operator to avoid duplicate condition blocks
  // This merges volume tags with the same operator into a single display group
  const groupedConditions = data.conditions.reduce(
    (acc, condition) => {
      const operator =
        'operator' in condition && typeof condition.operator === 'string'
          ? condition.operator
          : 'in';
      const conditionType = 'type' in condition ? String(condition.type) : '';
      const key = `${conditionType}-${operator}`;

      if (!acc[key]) {
        acc[key] = {
          type: conditionType,
          operator,
          values: [],
        };
      }

      // Merge values from this condition into the grouped condition
      acc[key].values.push(...condition.values);

      return acc;
    },
    {} as Record<
      string,
      { type: string; operator: string; values: (string | Record<string, unknown>)[] }
    >
  );

  const mergedConditions = Object.values(groupedConditions);

  // Determine maxVisible based on number of condition groups
  // If only one condition group, show all tags that fit; otherwise show 1 per group
  const totalConditionGroups = mergedConditions.length;
  const maxVisiblePerGroup = totalConditionGroups === 1 ? 4 : 1;

  return (
    <div className="flex gap-1.5 py-1 whitespace-nowrap">
      {mergedConditions.map((condition, index) => {
        const operatorText = getOperatorText(condition.operator, condition.type);

        return (
          <div key={index} className="flex items-center gap-1 shrink-0">
            {index > 0 && (
              <Typography
                variant="p3-reg"
                color="text-gray-700"
                className="whitespace-nowrap text-[14px]"
              >
                +
              </Typography>
            )}
            <Typography
              variant="p3-reg"
              color="text-gray-600"
              className="whitespace-nowrap text-[14px]"
            >
              {operatorText}
            </Typography>
            <RenderTagGroup<string | Record<string, unknown>>
              items={condition.values}
              getText={(value) => {
                // Handle string values
                if (typeof value === 'string') {
                  return value;
                }
                // Handle object values (volume tags)
                if (value && typeof value === 'object') {
                  // Check if it has name and value properties
                  if ('name' in value && 'value' in value) {
                    return `${value.name}: ${value.value}`;
                  }
                  // Fallback: show all key-value pairs
                  return (
                    Object.entries(value)
                      .filter(([_k, v]) => v !== undefined && v !== null)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(', ') || String(value)
                  );
                }
                return String(value);
              }}
              getUniqueKey={(value, idx) => `${data.ruleId}-${index}-value-${idx}`}
              variant={getTagVariant(condition.type)}
              maxVisible={maxVisiblePerGroup}
              tooltipIdPrefix={`${data.ruleId}-condition-${index}`}
              size="sm"
            />
          </div>
        );
      })}
    </div>
  );
}
